import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import { bindActionCreators } from 'redux'
import Button from 'components/common/button'
import { BUTTON_TYPES } from 'constants/button'
import BackdropModal from 'components/portal/backdrop-modal'
import useAddToBag from 'components/shared/useRedux/useAddToBag'
import openQuickAddBag from 'components/shared/product/quick-add/open-bag'
import ProductApi from 'lib/api/product'
import StateStorage from 'lib/state-storage'
import { withI18next } from 'lib/i18n/withI18next'
import { hasLocizeTranslation } from 'lib/utils/locize'
import createWorkwearAddToBag, { unavailableSize } from './request'
import styles from './style.scss'

const messages = {
  adding: ['Adding to bag…', 'กำลังเพิ่มลงถุง...'],
  retry: [
    "Couldn't add this item. Please try again.",
    'เพิ่มสินค้าลงถุงไม่สำเร็จ กรุณาลองอีกครั้ง',
  ],
  unavailable: [
    "This size couldn't be added. Choose another size or check your bag.",
    'ไม่สามารถเพิ่มไซซ์นี้ได้ กรุณาเลือกไซซ์อื่นหรือตรวจสอบถุงช้อปปิ้ง',
  ],
  uncertain: [
    "We couldn't confirm the update. Open your bag to check.",
    'ยังยืนยันการอัปเดตไม่ได้ กรุณาเปิดถุงช้อปปิ้งเพื่อตรวจสอบ',
  ],
  success: ['Added to bag', 'เพิ่มลงถุงแล้ว'],
}

export const openWorkwearBag = openQuickAddBag

export const WorkwearQuickAdd = ({
  productId,
  options,
  refData,
  addToBagAction,
  openBag,
  isThai,
  t,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [result, setResult] = useState({ status: 'idle', size: null })
  const [unavailableIds, setUnavailableIds] = useState([])
  const dialog = useRef()
  const request = useRef()
  if (!request.current) {
    request.current = createWorkwearAddToBag({
      productId,
      refData,
      getProduct: (id) =>
        ProductApi.getProduct({
          product_id: id,
          isGuestMode: StateStorage.isGuestMode(),
        }),
      addToBag: addToBagAction,
      onState: (next) => {
        setResult(next)
        if (next.status === 'success') setIsOpen(false)
        if (next.status === 'unavailable')
          setUnavailableIds((ids) => [...ids, next.size.id_product_attribute])
      },
    })
  }
  useEffect(() => () => request.current.dispose(), [])
  useEffect(() => {
    if (!isOpen) return undefined
    const previousFocus = document.activeElement
    const frame = requestAnimationFrame(() =>
      dialog.current?.querySelector('button:not([disabled])')?.focus(),
    )
    return () => {
      cancelAnimationFrame(frame)
      if (previousFocus?.isConnected) previousFocus.focus()
    }
  }, [isOpen])
  useEffect(() => {
    // A selected control may become disabled or disappear while adding.
    if (
      isOpen &&
      dialog.current &&
      (!dialog.current.contains(document.activeElement) ||
        document.activeElement?.disabled)
    )
      dialog.current.querySelector('button:not([disabled])')?.focus()
  }, [isOpen, result.status])

  const copy = (key, english, thai) =>
    hasLocizeTranslation(t, key, isThai ? thai : english)
  const statusCopy = messages[result.status]
  const pending = result.status === 'adding'
  const uncertain = result.status === 'uncertain'
  const message = statusCopy
    ? copy(`WORKWEAR_QUICK_ADD_${result.status.toUpperCase()}`, ...statusCopy)
    : ''
  const close = () => setIsOpen(false)
  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      close()
    }
    if (event.key === 'Tab') {
      const buttons = dialog.current.querySelectorAll('button:not([disabled])')
      const first = buttons[0]
      const last = buttons[buttons.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }

  return (
    <React.Fragment>
      <style jsx>{styles}</style>
      <Button
        type={BUTTON_TYPES.text}
        className="workwear-quick-add__trigger"
        cy="workwear-quick-add-button"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          if (result.status === 'success')
            setResult({ status: 'idle', size: null })
          setIsOpen(true)
        }}
      >
        {copy('ADD_TO_BAG', 'ADD TO BAG', 'เพิ่มลงถุง')}
      </Button>
      {!isOpen && result.status === 'success' && (
        <p className="workwear-quick-add__success" role="status">
          {message}
        </p>
      )}
      {isOpen && (
        <BackdropModal
          isOpen={isOpen}
          freezeBody
          zIndex={2000}
          onBackdropClick={close}
        >
          {/* The dialog owns Escape handling and traps Tab among its controls. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            className="workwear-quick-add__dialog"
            role="dialog"
            aria-modal="true"
            aria-label={copy(
              'Choose your size',
              'Choose your size',
              'เลือกไซซ์',
            )}
            onKeyDown={onKeyDown}
            ref={dialog}
          >
            <div className="workwear-quick-add__heading">
              <strong>
                {copy('Choose your size', 'Choose your size', 'เลือกไซซ์')}
              </strong>
              <button type="button" onClick={close}>
                {copy('CLOSE', 'Close', 'ปิด')}
              </button>
            </div>
            <div aria-busy={pending}>
              {(options || [])
                .filter((size) => size?.id_product_attribute)
                .map((size) => {
                  const id = size.id_product_attribute
                  const selected = result.size?.id_product_attribute === id
                  const unavailable =
                    unavailableSize(size) || unavailableIds.includes(id)
                  return (
                    <button
                      className="workwear-quick-add__size"
                      type="button"
                      key={id}
                      aria-pressed={selected}
                      disabled={pending || uncertain || unavailable}
                      onClick={() => request.current.add(size)}
                    >
                      <span>{size.size}</span>
                      {unavailable && (
                        <span>
                          {copy('SOLD_OUT', 'Unavailable', 'ไม่พร้อมจำหน่าย')}
                        </span>
                      )}
                    </button>
                  )
                })}
            </div>
            <p
              className="workwear-quick-add__status"
              role="status"
              aria-live="polite"
            >
              {message}
            </p>
            {result.status === 'retry' && (
              <Button
                onClick={() => request.current.add(result.size)}
                style={{ minHeight: 44 }}
              >
                {copy('WORKWEAR_QUICK_ADD_RETRY', 'Try again', 'ลองอีกครั้ง')}
              </Button>
            )}
            {(uncertain || result.status === 'unavailable') && (
              <Button
                onClick={() => {
                  close()
                  openBag()
                }}
                style={{ minHeight: 44 }}
              >
                {copy(
                  'WORKWEAR_QUICK_ADD_CHECK_BAG',
                  'Check bag',
                  'ตรวจสอบถุง',
                )}
              </Button>
            )}
          </div>
        </BackdropModal>
      )}
    </React.Fragment>
  )
}

WorkwearQuickAdd.defaultProps = { options: [], refData: null, isThai: false }
WorkwearQuickAdd.propTypes = {
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  refData: PropTypes.shape({}),
  addToBagAction: PropTypes.func.isRequired,
  openBag: PropTypes.func.isRequired,
  isThai: PropTypes.bool,
  t: PropTypes.func.isRequired,
}

// Remount for a new colour, shopper or locale; obsolete preparation cannot write.
const ContextualQuickAdd = ({ productId, requestContext, ...props }) => (
  <WorkwearQuickAdd
    key={`${productId}:${requestContext}`}
    productId={productId}
    {...props}
  />
)
ContextualQuickAdd.propTypes = {
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  requestContext: PropTypes.string.isRequired,
}

export default compose(
  connect(
    (state) => ({
      isThai: state.internationalization.language === 'th',
      requestContext: JSON.stringify([
        state.auth?.user?.id_customer,
        state.auth?.isGuestMode,
        state.internationalization,
      ]),
    }),
    (dispatch) =>
      bindActionCreators(
        { addToBagAction: useAddToBag, openBag: openWorkwearBag },
        dispatch,
      ),
  ),
  withI18next(),
)(ContextualQuickAdd)
